import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronLeft,
  FileArchive,
  Folder,
  Frame,
  ImageIcon,
  Images,
  Square,
  Sun,
  X,
} from "lucide-react";
import { useCuentaShell } from "./estilos/cuentaShell";
import { useVolverEnLaBanda } from "../hooks/useVolverEnLaBanda";
import { MAX_IMAGENES_POR_PRODUCTO } from "../lib/limiteImagenesProducto";

/** De dónde se llega y a dónde se vuelve por defecto: las dos formas de subir. */
const RUTA_SUBIR = "/mi-marca/productos/nuevo";

/**
 * Quien abre los consejos puede pedir que se vuelva a otro lado (la pantalla de un solo
 * producto, con lo que ya tenía cargado): `navigate(RUTA, { state: { volverA, volverEtiqueta } })`.
 */
export type VolverDeConsejos = { volverA: string; volverEtiqueta: string };

/**
 * Las cuatro claves: lo que hay que recordar antes de sacar la foto. `corto` es la versión
 * de una línea que usa el resumen de la pantalla de un solo producto
 * (ADR-2026-10-13-un-solo-producto-reestructurado).
 */
export const CLAVES: { icono: React.ReactNode; titulo: string; texto: string; corto: string }[] = [
  {
    icono: <Sun className="h-5 w-5" />,
    titulo: "Buena luz",
    texto: "Luz natural o pareja, sin sombras fuertes ni flash directo.",
    corto: "Natural y pareja",
  },
  {
    icono: <Square className="h-5 w-5" />,
    titulo: "Fondo limpio",
    texto: "Liso o despejado, sin otras cosas que distraigan.",
    corto: "Liso, sin distracciones",
  },
  {
    icono: <Frame className="h-5 w-5" />,
    titulo: "Producto entero",
    texto: "Que se vea completo y ocupe buena parte del cuadro.",
    corto: "Completo en el cuadro",
  },
  {
    icono: <Images className="h-5 w-5" />,
    titulo: "Varias vistas",
    texto: "De frente, de costado y de atrás: cuantas más, mejor.",
    corto: "Frente, costado y atrás",
  },
];

export type Ejemplo = {
  titulo: string;
  texto: string;
  /** Una palabra, para el resumen de la pantalla de un solo producto. */
  etiqueta: string;
  /** La foto. `filtro` la arruina a propósito para ilustrar un error (ver `MALAS`). */
  foto: { src: string; filtro?: string } | "varios-juntos";
};

/** Fotos de moda que funcionan, todas de `public/`. */
export const BUENAS: Ejemplo[] = [
  {
    titulo: "Entera y sobre fondo liso",
    etiqueta: "Entera",
    texto: "Se ve el producto completo y no hay nada alrededor que compita.",
    foto: { src: "/images/onboarding/product/instructions/buena-entera.jpg" },
  },
  {
    titulo: "Nítida y bien iluminada",
    etiqueta: "Nítida",
    texto: "Los colores y las texturas se ven tal cual son.",
    foto: { src: "/images/onboarding/product/instructions/buena-nitida.jpg" },
  },
  {
    titulo: "Puesta, sin mostrar la cara",
    etiqueta: "Puesta",
    texto: "Muestra cómo queda sin que la persona se lleve la atención.",
    foto: { src: "/images/onboarding/product/instructions/buena-puesta.jpg" },
  },
  {
    titulo: "De frente y bien estirada",
    etiqueta: "Estirada",
    texto: "Ideal para prendas: se entienden la forma y los detalles.",
    foto: { src: "/images/onboarding/product/instructions/buena-estirada.jpg" },
  },
];

/**
 * Fotos que conviene evitar. Las dos primeras son fotos malas de verdad (las del
 * onboarding). Las otras dos se arman con fotos buenas que ya existen: la cartera
 * oscurecida con un filtro y tres prendas en un mismo cuadro. No había otras fotos malas
 * de moda en el repo (ADR-2026-10-13-subir-productos-pantalla-de-consejos).
 */
export const MALAS: Ejemplo[] = [
  {
    titulo: "Movida o desenfocada",
    etiqueta: "Movida",
    texto: "La IA no llega a distinguir la forma ni los detalles del producto.",
    foto: { src: "/images/subir-productos/1.jpg" },
  },
  {
    titulo: "Producto cortado",
    etiqueta: "Cortada",
    texto: "Si falta una parte, la IA la tiene que inventar y puede no coincidir.",
    foto: { src: "/images/subir-productos/2.jpg" },
  },
  {
    titulo: "Con poca luz",
    etiqueta: "Oscura",
    texto: "Los colores cambian y se pierden las texturas.",
    foto: { src: "/images/subir-productos/3.jpg" },
  },
  {
    titulo: "Varios productos juntos",
    etiqueta: "Varios juntos",
    texto: "Una foto, un producto: si hay más de uno, la IA no sabe cuál es.",
    foto: { src: "/images/subir-productos/4.jpg" },
  },
];

/**
 * "Consejos para tus fotos", la pantalla a la que lleva la tarjeta azul de "Subir
 * productos" — ADR-2026-10-13-subir-productos-pantalla-de-consejos.
 *
 * Tres partes, una debajo de la otra: las cuatro claves (luz, fondo, producto entero,
 * vistas), cuatro fotos que funcionan (tilde verde, por qué) y cuatro que conviene evitar
 * (cruz roja, cuál es el error). El patrón de Vinted, Uber Eats y eBay: la foto con su
 * marca y una línea que explica. Al final, "Empezar a subir" vuelve a las dos formas de
 * subir.
 *
 * Mismo contenedor que Mi marca y "Subir productos", y la misma entrada: el fundido del
 * shell al llegar y los bloques escalonados (70ms entre uno y otro).
 */
export const ConsejosDeFotos = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const desde = (state as Partial<VolverDeConsejos> | null) ?? null;
  const destino = desde?.volverA ?? RUTA_SUBIR;
  const etiquetaDeVolver = desde?.volverEtiqueta ?? "Subir productos";
  const { conFadeDeSalida } = useCuentaShell();
  const volver = () => conFadeDeSalida(() => navigate(destino));
  useVolverEnLaBanda(etiquetaDeVolver, volver);

  return (
    <div className="px-4 pt-2 sm:px-8 sm:pt-2 max-w-content mx-auto pb-[calc(env(safe-area-inset-bottom,0px)+2rem)]">
      <div>
        {/* Cabecera: volver (desde `md`; abajo lo pone el cascarón), título y bajada. */}
        <div className="aparece-tras-esqueleto">
          <button
            type="button"
            onClick={volver}
            className="mb-4 hidden h-9 items-center gap-1 rounded-full bg-black/[0.05] pl-2.5 pr-3.5 text-[14px] font-medium text-black/60 transition-colors hover:bg-black/[0.09] hover:text-[#16181D] md:inline-flex"
          >
            <ChevronLeft className="h-4 w-4" />
            {etiquetaDeVolver}
          </button>
          <h1 className="text-[26px] font-bold leading-tight tracking-[-0.02em] text-[#16181D]">
            Cómo sacar buenas fotos
          </h1>
          <p className="mt-1.5 max-w-[640px] text-[14px] leading-snug text-black/50">
            Con buenas fotos, la IA entiende mejor tu producto y todo lo que crees sale mejor.
          </p>
        </div>

        <CuerpoDeConsejos />

        {/* De vuelta a elegir cómo subir: el mismo azul que la tarjeta que trajo hasta acá. */}
        <TarjetaParaCargar onClick={volver} retraso={350} />
      </div>
    </div>
  );
};

/**
 * Lo que enseña la pantalla: las cuatro claves y las fotos que funcionan y las que no. Lo
 * usan esta pantalla y el pop-up de consejos (`ModalDeConsejos`), que se abre desde "Varios
 * productos a la vez" sin salir de la carga — ADR-2026-10-14-consejos-en-un-pop-up-desde-varios.
 */
export const CuerpoDeConsejos: React.FC<{ enVentana?: boolean }> = ({ enVentana = false }) => (
  <>
    {/* 1. Las claves. */}
    <section
      className={`aparece-tras-esqueleto ${enVentana ? "" : "mt-7"}`}
      style={{ animationDelay: "70ms" }}
      aria-label="Lo que hace una buena foto"
    >
      {/* Un solo panel partido en cuatro, con números grandes: se lee como una lista
          para revisar y no como cuatro tarjetas sueltas, que competían con las fotos de
          abajo (ADR-2026-10-13-consejos-titulos-y-claves). Las divisiones son bordes de
          cada ítem: 2×2 en el teléfono y una fila de cuatro desde `lg`. */}
      <ol className="grid grid-cols-2 overflow-hidden rounded-[28px] bg-gradient-to-br from-[#EEF2FF] via-[#F5F7FF] to-white shadow-[0_0_0_1px_rgba(22,68,235,0.10)] lg:grid-cols-4">
        {CLAVES.map((c, i) => (
          <li
            key={c.titulo}
            className={`p-4 sm:p-6 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b lg:border-b-0" : ""} ${
              i === 1 ? "lg:border-r" : ""
            } border-[#1644EB]/10`}
          >
            <span className="block text-[30px] font-bold leading-none tracking-[-0.03em] text-brand-primary sm:text-[38px]">
              {String(i + 1).padStart(2, "0")}
            </span>
            <p className="mt-4 flex items-center gap-1.5 text-[15px] font-semibold leading-tight text-[#16181D] sm:text-[16px]">
              <span className="shrink-0 text-brand-primary [&>svg]:h-4 [&>svg]:w-4">
                {c.icono}
              </span>
              {c.titulo}
            </p>
            <p className="mt-1.5 text-[13px] leading-snug text-black/55 sm:text-[13.5px]">
              {c.texto}
            </p>
          </li>
        ))}
      </ol>
    </section>

    {/* 2. Las que funcionan. */}
    <SeccionDeEjemplos
      id="consejos-buenas"
      titulo="Fotos que funcionan"
      bajada="Así la IA entiende bien tu producto."
      buena
      ejemplos={BUENAS}
      retraso={140}
    />

    {/* 3. Las que conviene evitar. */}
    <SeccionDeEjemplos
      id="consejos-malas"
      titulo="Fotos que conviene evitar"
      bajada="Estos errores hacen que los resultados salgan peor."
      buena={false}
      ejemplos={MALAS}
      retraso={210}
    />

    {/* 4. Muchos productos de una vez. */}
    <SeccionDelZip />
  </>
);

/** El ejemplo del árbol: dos productos con sus fotos. */
const CARPETAS_DE_EJEMPLO = [
  { nombre: "Remera negra", fotos: ["1-frente.jpg", "2-espalda.jpg", "3-detalle.jpg"] },
  { nombre: "Buzo gris", fotos: ["1-frente.jpg", "2-costado.jpg"] },
];

/**
 * Cómo subir muchos productos de una vez con un .zip (o soltando las carpetas en la
 * compu): cada carpeta es un producto con su nombre
 * (ADR-2026-10-16-subir-varios-con-zip-y-carpetas). A la izquierda, el árbol de un
 * .zip de ejemplo; a la derecha, las tres reglas.
 */
const SeccionDelZip: React.FC = () => (
  <section
    className="aparece-tras-esqueleto mt-12"
    style={{ animationDelay: "280ms" }}
    aria-labelledby="consejos-zip"
  >
    <TituloDeSeccion
      id="consejos-zip"
      titulo="¿Muchos productos? Subilos en un .zip"
      bajada="Armá una carpeta por producto con sus fotos adentro, comprimilas y subí el .zip."
    />
    <div className="grid gap-3 lg:grid-cols-2 lg:gap-4">
      <div
        aria-label="Ejemplo de cómo armar el .zip"
        className="rounded-[28px] bg-gradient-to-br from-[#EEF2FF] via-[#F5F7FF] to-white p-5 shadow-[0_0_0_1px_rgba(22,68,235,0.10)] sm:p-6"
      >
        <p className="flex items-center gap-2 text-[14px] font-semibold text-[#16181D]">
          <FileArchive className="h-4 w-4 text-brand-primary" />
          productos.zip
        </p>
        <ul className="mt-3 space-y-3 border-l border-[#1644EB]/15 pl-4">
          {CARPETAS_DE_EJEMPLO.map((c) => (
            <li key={c.nombre}>
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] font-medium text-[#16181D]">
                <Folder className="h-4 w-4 shrink-0 text-brand-primary" />
                {c.nombre}
                <span className="rounded-full bg-white px-2 py-0.5 text-[11.5px] font-medium text-brand-primary shadow-[0_0_0_1px_rgba(22,68,235,0.12)]">
                  1 producto
                </span>
              </p>
              <ul className="mt-1.5 space-y-1 pl-6">
                {c.fotos.map((f, i) => (
                  <li
                    key={f}
                    className="flex items-center gap-1.5 text-[12.5px] text-black/55"
                  >
                    <ImageIcon className="h-3.5 w-3.5 shrink-0 text-black/35" />
                    {f}
                    {i === 0 && <span className="text-black/35">· portada</span>}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
      <ol className="grid gap-3">
        {[
          {
            titulo: "Una carpeta, un producto",
            texto: "El producto se llama como la carpeta, y sus fotos son las de adentro.",
          },
          {
            titulo: `Hasta ${MAX_IMAGENES_POR_PRODUCTO} fotos por carpeta`,
            texto: "La primera por orden de nombre es la portada: numeralas para elegirla.",
          },
          {
            titulo: "En la compu, sin comprimir",
            texto: "También podés arrastrar las carpetas directo. Las fotos sueltas son un producto cada una.",
          },
        ].map((r) => (
          <li
            key={r.titulo}
            className="rounded-2xl bg-white p-4 shadow-[0_0_0_1px_rgba(0,0,0,0.06)]"
          >
            <p className="flex items-center gap-1.5 text-[14px] font-semibold leading-tight text-[#16181D]">
              <Check className="h-4 w-4 shrink-0 text-brand-primary" />
              {r.titulo}
            </p>
            <p className="mt-1 text-[13px] leading-snug text-black/50">{r.texto}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

/**
 * Una tanda de ejemplos (las que funcionan o las que no): título con su marca y una
 * grilla de fotos con la tilde o la cruz y, debajo, qué tiene de bueno o cuál es el error.
 */
const SeccionDeEjemplos: React.FC<{
  id: string;
  titulo: string;
  bajada: string;
  buena: boolean;
  ejemplos: Ejemplo[];
  retraso: number;
}> = ({ id, titulo, bajada, buena, ejemplos, retraso }) => (
  <section
    className="aparece-tras-esqueleto mt-12"
    style={{ animationDelay: `${retraso}ms` }}
    aria-labelledby={id}
  >
    <TituloDeSeccion
      id={id}
      titulo={titulo}
      bajada={bajada}
      marca={<Marca buena={buena} />}
    />
    <ul className="grid grid-cols-2 gap-x-3 gap-y-5 lg:grid-cols-4 lg:gap-x-4">
      {ejemplos.map((e) => (
        <li key={e.titulo}>
          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#F1F1F1]">
            <FotoDeEjemplo foto={e.foto} />
            {/* Borde fino y neutro encima de la foto (un ring del contenedor queda debajo de
                la imagen): separa las fotos claras del fondo. Sin verde ni rojo: la marca ya
                dice cuál es cuál (ADR-2026-10-13-consejos-fotos-sin-borde-de-color). */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/[0.06]"
            />
            <span className="absolute bottom-2.5 left-2.5">
              <Marca buena={buena} />
            </span>
          </div>
          <p className="mt-2.5 text-[14px] font-semibold leading-tight text-[#16181D]">
            {e.titulo}
          </p>
          <p className="mt-1 text-[13px] leading-snug text-black/50">{e.texto}</p>
        </li>
      ))}
    </ul>
  </section>
);

/**
 * El título de cada parte, más grande que el de una tarjeta (20px) y con una línea debajo:
 * las tres partes se leen como capítulos de la pantalla. `marca` es la tilde o la cruz.
 */
const TituloDeSeccion: React.FC<{
  id: string;
  titulo: string;
  bajada: string;
  marca?: React.ReactNode;
}> = ({ id, titulo, bajada, marca }) => (
  <div className="mb-4 flex items-start gap-3">
    {marca && <span className="mt-0.5">{marca}</span>}
    <div className="min-w-0">
      <h2
        id={id}
        className="text-[19px] font-semibold leading-tight tracking-[-0.01em] text-[#16181D] sm:text-[21px]"
      >
        {titulo}
      </h2>
      <p className="mt-1 text-[13.5px] leading-snug text-black/50">{bajada}</p>
    </div>
  </div>
);

/** La tilde verde o la cruz roja. */
const Marca: React.FC<{ buena: boolean }> = ({ buena }) => (
  <span
    aria-label={buena ? "Funciona" : "Evitar"}
    className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white shadow-[0_2px_6px_rgba(16,24,40,0.2)] ring-2 ring-white ${
      buena ? "bg-success" : "bg-error"
    }`}
  >
    {buena ? <Check className="h-4 w-4" strokeWidth={3} /> : <X className="h-4 w-4" strokeWidth={3} />}
  </span>
);

/**
 * La foto de un ejemplo. "varios-juntos" arma el error de tres prendas distintas en un
 * mismo cuadro (jean, remera y zapatillas, encimadas como sobre una cama).
 */
export const FotoDeEjemplo: React.FC<{ foto: Ejemplo["foto"] }> = ({ foto }) => {
  if (foto === "varios-juntos") {
    return (
      <span aria-hidden className="absolute inset-0 bg-[#ECE8E2]">
        <img
          src="/images/subir-productos/jean.jpg"
          alt=""
          loading="lazy"
          className="absolute left-[2%] top-[6%] w-[62%] -rotate-12 mix-blend-multiply"
        />
        <img
          src="/images/subir-productos/remera.jpg"
          alt=""
          loading="lazy"
          className="absolute right-[0%] top-[16%] w-[58%] rotate-[8deg] mix-blend-multiply"
        />
        <img
          src="/images/subir-productos/zapatillas.jpg"
          alt=""
          loading="lazy"
          className="absolute bottom-[4%] left-[22%] w-[56%] -rotate-3 mix-blend-multiply"
        />
      </span>
    );
  }
  return (
    <img
      src={foto.src}
      alt=""
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover"
      style={foto.filtro ? { filter: foto.filtro } : undefined}
    />
  );
};

/** Tres productos distintos de moda, en abanico: "productos" de un vistazo. */
const FOTOS_PARA_CARGAR = [
  "/images/multi-vistas/zapatillas/perspectiva.webp",
  "/images/subir-productos/jean.png",
  "/images/subir-productos/remera.png",
];

/**
 * El cierre de los consejos: invita a subir los productos —
 * ADR-2026-10-13-consejos-cierre-para-cargar. Es la misma tarjeta azul que "Sacale el
 * máximo a tus fotos" de la pantalla anterior (degradé de marca, texto blanco, botón
 * blanco, toda la tarjeta tocable), así el ida y vuelta se lee como un par. En vez de una
 * foto buena y una mala lleva tres productos en abanico, y el texto dice lo que sigue:
 * subirlos. Lleva a las dos formas de subir.
 */
const TarjetaParaCargar: React.FC<{ onClick: () => void; retraso: number }> = ({
  onClick,
  retraso,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="aparece-tras-esqueleto group relative mt-12 flex w-full flex-col gap-5 overflow-hidden rounded-[28px] bg-gradient-to-br from-[#1644EB] via-[#2F5BFF] to-[#5B7BFF] p-5 text-left text-white shadow-[0_18px_40px_-18px_rgba(22,68,235,0.65)] transition-shadow duration-300 hover:shadow-[0_26px_56px_-18px_rgba(22,68,235,0.8)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 sm:flex-row sm:items-center sm:gap-6 sm:px-6 sm:py-5"
    style={{ animationDelay: `${retraso}ms` }}
  >
    <span
      aria-hidden
      className="pointer-events-none absolute -left-10 -top-16 h-48 w-48 rounded-full bg-white/15 blur-3xl"
    />
    <span
      aria-hidden
      className="pointer-events-none absolute -bottom-20 right-24 h-48 w-64 rounded-full bg-[#9DB1FF]/30 blur-3xl"
    />
    <span aria-hidden className="relative flex shrink-0 items-center pl-1">
      {FOTOS_PARA_CARGAR.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          loading="lazy"
          className={`h-[72px] w-[60px] rounded-xl bg-white object-cover shadow-[0_10px_22px_-8px_rgba(2,6,43,0.55)] ring-[3px] ring-white transition-transform duration-500 ease-[cubic-bezier(0.3,0.8,0.3,1)] motion-reduce:transition-none sm:h-[84px] sm:w-[70px] ${
            i === 0
              ? "-rotate-6 group-hover:-rotate-[10deg]"
              : i === 1
                ? "relative z-10 -ml-4 group-hover:-translate-y-1"
                : "-ml-4 rotate-6 group-hover:rotate-[10deg]"
          }`}
        />
      ))}
    </span>
    <span className="relative min-w-0 flex-1">
      <span className="block text-[18px] font-semibold leading-tight sm:text-[19px]">
        Ahora sí, subí tus productos
      </span>
      <span className="mt-1 block text-[13.5px] leading-snug text-white/80">
        Elegí si cargás uno solo o varios a la vez, y aplicá lo que viste acá.
      </span>
    </span>
    <span className="relative inline-flex h-11 shrink-0 items-center justify-center gap-2 self-stretch rounded-full bg-white px-5 text-[14.5px] font-semibold text-brand-primary shadow-[0_6px_16px_-6px_rgba(2,6,43,0.4)] transition-colors duration-300 group-hover:bg-[#EEF2FF] sm:self-auto">
      Empezar a cargar
      <ArrowRight className="h-4 w-4" />
    </span>
  </button>
);
